import React, { useRef, useState, useEffect } from 'react';

import Icon from 'views/site/Icon.js';
import 'react-phone-number-input/style.css';

import { AsYouTypeFormatter } from 'google-libphonenumber';
import Appearance from 'styles/Appearance.js';
import PhoneInput from 'react-phone-number-input';
import ProgressBar from 'views/ProgressBar.js';
import Utils from 'files/Utils.js';

const TextField = React.forwardRef(({
    append, appendContent, autoCapitalize, autoComplete, autoCorrect, className, containerStyle, expandWithText, fieldStyle, format, focused, icon, iconStyle, insetLabel, insetLabelStyle, insetContainerStyle, isSecure, isValid, loading, multiline, onChange, onBlur, onFocus, onKeyDown, onClick, onValidate, placeholder, prepend, prependContent, spellCheck, useDelay, value
}, ref) => {

    const fieldRef = useRef(null);
    const [active, setActive] = useState(focused);
    const [countryCode, setCountryCode] = useState(null);
    const [formatter, setFormatter] = useState(null);
    const [textTimeout, setTextTimeout] = useState(false);
    const [text, setText] = useState(value !== null && value !== undefined ? value : '');

    const onFieldClick = () => {
        setActive(true);
        if(typeof(onClick) === 'function') {
            onClick();
        }
    }

    const onFieldBlur = () => {
        setActive(false);
        if(typeof(onBlur) === 'function') {
            onBlur();
        }
    }

    const onPhoneInputTextChange = text => {

        // clear previous timeout if present
        if(textTimeout) {
            clearTimeout(textTimeout);
        }

        // set text with timeout if applicable
        setText(text);
        setTextTimeout(setTimeout(() => {
            if(typeof(onChange) === 'function') {
                onChange(text);
            }
        }, useDelay === true ? 250 : 0));
    }

    const onTextChange = evt => {

        // clear previous timeout if present
        if(textTimeout) {
            clearTimeout(textTimeout);
        }

        let newText = evt.target.value.replace(prepend || '', '').replace(append || '', '');
        switch(format) {
            case 'double':
            newText = parseFloat(newText.replace(/[^\d.-]/g,''));
            break;

            case 'integer':
            newText = parseInt(newText.replace(/[^\d.-]/g,''));
            break;

            case 'number':
            newText = newText.replace(/[^\d.-]/g,'');
            break;

            default:
            if(typeof(format) === 'function') {
                newText = format(newText);
            }
        }

        // set text with timeout if applicable
        setText(newText);
        setTextTimeout(setTimeout(() => {
            if(typeof(onChange) === 'function') {
                onChange(newText);
            }
        }, useDelay === true ? 250 : 0));
    }

    const onKeyDownPress = evt => {
        if(typeof(onKeyDown) === 'function') {

            // prepare values to be used after timeout
            let keyCode = evt.keyCode;
            let value = evt.target.value;
            
            // determine next steps using current keycode
            setTimeout(() => {
                switch(keyCode) {
                    case 13:
                    onKeyDown('enter');
                    break;

                    case 8:
                    onKeyDown('backspace');
                    break;

                    default:
                    onKeyDown(value);
                }
            }, useDelay === false ? 0 : 250);
        }
    }

    const getContent = () => {
        if(format !== 'phone_number') {
            return getField();
        }
        return (
            <div
            className={`dummy-field ${window.theme} ${active ? 'active':''} ${className || ''}`}
            style={{
                alignItems: 'center',
                backgroundColor: Appearance.colors.textField(),
                borderColor: Appearance.colors.divider(),
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
                height: expandWithText ? 'auto' : 35,
                minHeight: 35,
                position: 'relative',
                ...containerStyle
            }}>
                <PhoneInput
                autoComplete={'tel'}
                countryCallingCodeEditable={false}
                defaultCountry={countryCode}
                international={true}
                onChange={onPhoneInputTextChange}
                placeholder={placeholder}
                style={{
                    width: '100%'
                }}
                value={text}/>
                {appendContent}
            </div>
        )
    }

    const getField = () => {
        return (
            <div
            className={`dummy-field ${window.theme} ${active ? 'active':''} ${className || ''}`}
            style={{
                alignItems: 'center',
                backgroundColor: Appearance.colors.textField(),
                borderColor: Appearance.colors.divider(),
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
                height: expandWithText ? 'auto' : 35,
                minHeight: 35,
                position: 'relative',
                ...containerStyle
            }}>
                {getIcon(icon) && (
                    <div style={{
                        paddingRight: 8,
                        marginLeft: 4
                    }}>
                        <Icon
                        color={Appearance.colors.icon()}
                        name={getIcon(icon)}
                        size={13}
                        style={iconStyle} />
                    </div>
                )}
                {insetLabel && (
                    <div style={{
                        marginRight: 8,
                        paddingRight: 8,
                        borderRight: `1px solid ${Appearance.colors.softBorder()}`,
                        ...insetContainerStyle
                    }}>
                        <span style={{
                            fontSize: 10,
                            fontWeight: '600',
                            color: Appearance.colors.subText(),
                            ...insetLabelStyle
                        }}>{insetLabel}</span>
                    </div>
                )}
                {getFieldComponent()}
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2
                }}>
                    <div style={{
                        display: loading ? 'block' : 'none',
                        overflow: 'hidden',
                        borderRadius: 2,
                        height: 2
                    }}>
                        <ProgressBar />
                    </div>
                </div>
                {appendContent}
                {typeof(onValidate) === 'function' && onValidate(text) && (
                    <img
                    src={'images/checkmark-small-green.png'}
                    style={{
                        width: 15,
                        height: 15,
                        objectFit: 'contain',
                        marginLeft: 8
                    }} />
                )}
            </div>
        )
    }

    const getFieldComponent = () => {
        if(typeof(onClick) === 'function') {
            return (
                <span
                onClick={onFieldClick}
                className={'cursor-pointer'}
                style={{
                    flexGrow: 1,
                    borderWidth: 0,
                    fontSize: 14,
                    fontWeight: 500,
                    color: value ? Appearance.colors.text() : Appearance.colors.subText(),
                    backgroundColor: Appearance.colors.transparent,
                    ...fieldStyle
                }}>{value || placeholder}</span>
            )
        }
        if(isSecure || expandWithText !== true) {
            return (
                <input
                ref={ref}
                className={`dummy-field-input ${window.theme}`}
                onBlur={onFieldBlur}
                onClick={onFieldClick}
                onChange={onTextChange}
                onKeyDown={onKeyDownPress}
                value={(prepend || '') + (text || '') + (append || '')}
                placeholder={placeholder || ''}
                type={isSecure ? 'password' : 'text'}
                autoComplete={autoComplete || 'off'}
                autoCorrect={autoCorrect === false ? 'off':'on'}
                autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
                spellCheck={spellCheck === false ? 'false':'true'}
                inputMode={getInputMode()}
                style={{
                    border: 'none',
                    width: '100%',
                    height: 'auto',
                    fontSize: 14,
                    fontWeight: 500,
                    color: Appearance.colors.text(),
                    backgroundColor: Appearance.colors.transparent,
                    ...fieldStyle
                }} />
            )
        }

        return (
            <textarea
            ref={ref}
            className={`dummy-field-input ${window.theme}`}
            onBlur={onFieldBlur}
            onClick={onFieldClick}
            onChange={onTextChange}
            onKeyDown={onKeyDownPress}
            value={(prepend || '') + (text || '') + (append || '')}
            placeholder={placeholder || ''}
            type={isSecure === true ? 'password':'text'}
            autoComplete={autoComplete || 'off'}
            autoCorrect={autoCorrect === false ? 'off':'on'}
            autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
            spellCheck={spellCheck === false ? 'false':'true'}
            style={{
                border: 'none',
                width: '100%',
                height: 'auto',
                minHeight: 125,
                fontSize: 14,
                fontWeight: 500,
                resize: 'none',
                verticalAlign: 'middle',
                color: Appearance.colors.text(),
                paddingTop: expandWithText ? 0 : 18.5,
                backgroundColor: Appearance.colors.transparent,
                ...fieldStyle
            }} />
        )
    }

    const getInputMode = () => {
        if([ 'integer' ].includes(format)) {
            return 'numeric';
        }
        if([ 'double', 'integer', 'number', 'phone_number' ].includes(format)) {
            return 'decimal';
        }
        return null;
    }

    const setupFormat = () => {
        if(format === 'phone_number') {
            try {
                // setup formatter for inline text formatting
                let formatter = new AsYouTypeFormatter(countryCode || 'US');
                setFormatter(formatter);

                // set default country code using navigator locale
                let locale = new Intl.Locale(navigator.language);
                setCountryCode(locale.region);
            } catch(e) {
                console.error(e.message);
                setCountryCode('US');
            }
        }
    }

    useEffect(() => {
        setupFormat();
    }, [format]);

    useEffect(() => {
        setActive(focused);
    }, [focused])

    useEffect(() => {
        setText(value || '');
    }, [value]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%'
        }}>
            {prependContent}
            {getContent()}
        </div>
    )
})

export default TextField;

export const TextFieldContainer = React.forwardRef(({ containerStyle, fieldStyle, icon, iconStyle, loading, onClick, value }, ref) => {

    const [active, setActive] = useState(false);

    const onFieldClick = async () => {
        try {
            setActive(true);
            if(typeof(onClick) === 'function') {
                onClick();
            }
            await Utils.sleep(1);
            setActive(false);

        } catch(e) {
            console.error(e.message);
        }
    }

    const getContent = () => {
        return (
            <div
            className={`dummy-field ${window.theme} ${active ? 'active':''}`}
            style={{
                alignItems: 'center',
                backgroundColor: Appearance.colors.textField(),
                borderColor: Appearance.colors.divider(),
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
                height: 35,
                minHeight: 35,
                position: 'relative',
                ...containerStyle
            }}>
                {getIcon(icon) && (
                    <div style={{
                        paddingRight: 8,
                        marginLeft: 4
                    }}>
                        <Icon
                        color={Appearance.colors.icon()}
                        name={getIcon(icon)}
                        size={13}
                        style={iconStyle} />
                    </div>
                )}
                <span
                onClick={onFieldClick}
                className={'cursor-pointer'}
                style={{
                    flexGrow: 1,
                    borderWidth: 0,
                    fontSize: 14,
                    fontWeight: 500,
                    color: value ? Appearance.colors.text() : Appearance.colors.subText(),
                    backgroundColor: Appearance.colors.transparent,
                    ...fieldStyle
                }}>{value}</span>
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2
                }}>
                    <div style={{
                        display: loading ? 'block' : 'none',
                        overflow: 'hidden',
                        borderRadius: 2,
                        height: 2
                    }}>
                        <ProgressBar />
                    </div>
                </div>
            </div>
        )
    }

    return getContent();
})

export const getIcon = (icon) => {
    switch(icon) {

        case 'user':
        return 'person-fill';

        case 'spouse':
        return 'house-heart-fill';

        case 'user-secret':
        return 'person-badge';

        case 'email':
        return 'envelope-fill';

        case 'phone':
        return 'telephone-fill';

        case 'calendar':
        return 'calendar-event';

        case 'house':
        return 'house-door-fill';

        case 'search':
        return 'search';

        case 'lock':
        return 'lock-fill';

        case 'location':
        return 'cursor-fill';

        case 'payment':
        return 'cash-stack';

        case 'asterick':
        return 'asterisk';

        case 'route':
        return 'signpost-split-fill';

        case 'guardian':
        return 'people';

        case 'title':
        return 'pencil-square';

        case 'description':
        return 'list-nested';

        case 'featured':
        return 'heart-fill';

        case 'wifi':
        return 'wifi';

        case 'pattern':
        return 'grid-fill'

        case 'office':
        return 'briefcase-fill'

        default:
        return;
    }
}
